import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { facilityService } from "../../services/facilityService";
import { useAuth } from "../../contexts/AuthContext";
import authService from "../../services/authService";

const FacilitiesList = () => {
  const [loading, setLoading] = useState(true);
  const [facilities, setFacilities] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    facility: null,
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  // Get auth context
  const { user, isSuperAdmin } = useAuth();

  useEffect(() => {
    fetchFacilities();
    fetchCurrentUser();
  }, [page, rowsPerPage, searchQuery]);

  const fetchCurrentUser = async () => {
    try {
      const user = await authService.getCurrentUser();
      setCurrentUser(user);
    } catch (error) {
      console.error("Error fetching current user:", error);
    }
  };

  const fetchFacilities = async () => {
    setLoading(true);
    try {
      const response = await facilityService.getFacilities(
        page * rowsPerPage,
        rowsPerPage
      );

      if (Array.isArray(response)) {
        // Filter by search query on frontend if needed
        let filteredData = response;
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          filteredData = response.filter(
            (facility) =>
              facility.company_name?.toLowerCase().includes(query) ||
              facility.email?.toLowerCase().includes(query) ||
              facility.phone_number?.toLowerCase().includes(query) ||
              facility.address?.toLowerCase().includes(query) ||
              facility.subscription_type?.toLowerCase().includes(query)
          );
        }

        setFacilities(filteredData);
        setTotalCount(filteredData.length);
      } else {
        setFacilities([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching facilities:", error);
      setFacilities([]);
      setError("Failed to load facilities");
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const canManageFacilities = () => {
    return isSuperAdmin;
  };

  const canViewFacility = (facility) => {
    return isSuperAdmin || user?.facility_id === facility.id;
  };

  const canEditFacility = (facility) => {
    return isSuperAdmin || user?.facility_id === facility.id;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0); // Reset to first page on new search
  };

  const handleDeleteClick = (facility) => {
    setDeleteDialog({ open: true, facility });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, facility: null });
  };

  const handleDeleteConfirm = async () => {
    try {
      await facilityService.deleteFacility(deleteDialog.facility.id);

      // Remove the deleted item from the local state
      setFacilities((prev) =>
        prev.filter((facility) => facility.id !== deleteDialog.facility.id)
      );
      setTotalCount((prev) => Math.max(0, prev - 1));

      setSuccess(
        `Facility "${deleteDialog.facility.company_name}" deleted successfully`
      );
      setDeleteDialog({ open: false, facility: null });

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error deleting facility:", error);
      setError(error.message || "Failed to delete facility");
      setDeleteDialog({ open: false, facility: null });
    }
  };

  const getSubscriptionColor = (subscriptionType) => {
    const colors = {
      basic: "default",
      standard: "primary",
      premium: "secondary",
    };
    return colors[subscriptionType?.toLowerCase()] || "default";
  };

  const formatSubscription = (subscriptionType) => {
    if (!subscriptionType) return "N/A";
    return subscriptionType.charAt(0).toUpperCase() + subscriptionType.slice(1);
  };

  const formatAdminLevel = (level) => {
    const levels = {
      1: "Basic",
      2: "Standard",
      3: "Advanced",
      4: "Super Admin",
    };
    return levels[level] || "Unknown";
  };

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight="bold">
          Facilities Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate("/facilities/create")}
          disabled={!canManageFacilities()}
        >
          Add Facility
        </Button>
      </Box>

      {/* Quick Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid  size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <BusinessIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">{totalCount}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Facilities
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid  size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PeopleIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">
                    {facilities.reduce(
                      (total, f) => total + (f.users?.length || 0),
                      0
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid  size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AssignmentIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">
                    {facilities.reduce(
                      (total, f) => total + (f.orders?.length || 0),
                      0
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Orders
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search facilities by name, email, phone, address, or subscription type"
          variant="outlined"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {loading ? (
        <LinearProgress />
      ) : (
        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Company Name</TableCell>
                <TableCell>Contact Info</TableCell>
                <TableCell>Subscription</TableCell>
                <TableCell>Admin Level</TableCell>
                <TableCell>Stats</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {facilities.length > 0 ? (
                facilities
                  .slice(page * rowsPerPage, (page + 1) * rowsPerPage)
                  .map((facility) => (
                    <TableRow key={facility.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {facility.company_name || "N/A"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {facility.business_license &&
                              `License: ${facility.business_license}`}
                            {facility.npi && ` | NPI: ${facility.npi}`}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {facility.email || "N/A"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {facility.phone_number || "N/A"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {facility.address || "N/A"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatSubscription(facility.subscription_type)}
                          color={getSubscriptionColor(
                            facility.subscription_type
                          )}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatAdminLevel(facility.admin_level)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            Users: {facility.users?.length || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            MPs: {facility.medical_professionals?.length || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Orders: {facility.orders?.length || 0}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="primary"
                          onClick={() => navigate(`/facilities/${facility.id}`)}
                          title="View Details"
                          disabled={!canViewFacility(facility)}
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          color="secondary"
                          onClick={() =>
                            navigate(`/facilities/${facility.id}/edit`)
                          }
                          title="Edit"
                          disabled={!canEditFacility(facility)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(facility)}
                          title="Delete"
                          disabled={!isSuperAdmin}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" color="text.secondary">
                      No facilities found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete facility "
            {deleteDialog.facility?.company_name}"? This action cannot be
            undone.
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Warning: This will also affect all associated users, medical
            professionals, and orders.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete Facility
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FacilitiesList;
